export type MarketSpec = {
  id: 'US' | 'IL' | 'GENERIC';
  timeZone: string; // IANA tz
  openMinutes: number; // minutes since midnight in market tz
  closeMinutes: number; // minutes since midnight in market tz
  preMarketStartMinutes?: number | null; // minutes since midnight in market tz
  openWeekdays: Array<'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat'>;
};

export type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat';
};

const WEEKDAYS: ZonedParts['weekday'][] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function getMarketSpecForExchange(exchangeRaw: string | null | undefined): MarketSpec {
  const exchange = String(exchangeRaw ?? '').trim().toUpperCase();

  // US equities
  if (exchange.includes('NASDAQ') || exchange.includes('NYSE') || exchange.includes('AMEX')) {
    return {
      id: 'US',
      timeZone: 'America/New_York',
      preMarketStartMinutes: 4 * 60,
      openMinutes: 9 * 60 + 30,
      closeMinutes: 16 * 60,
      openWeekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    };
  }

  // Tel Aviv Stock Exchange (best-effort; no holidays)
  if (exchange.includes('TASE') || exchange.includes('TLV') || exchange.includes('TEL AVIV') || exchange.includes('TA')) {
    return {
      id: 'IL',
      timeZone: 'Asia/Jerusalem',
      openMinutes: 9 * 60 + 30,
      closeMinutes: 17 * 60 + 30,
      preMarketStartMinutes: null,
      openWeekdays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu'],
    };
  }

  // Fallback: treat as US
  return {
    id: 'GENERIC',
    timeZone: 'America/New_York',
    preMarketStartMinutes: 4 * 60,
    openMinutes: 9 * 60 + 30,
    closeMinutes: 16 * 60,
    openWeekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  };
}

export function getZonedParts(date: Date, timeZone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short',
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value;
  const year = Number(get('year'));
  const month = Number(get('month'));
  const day = Number(get('day'));
  const hour = Number(get('hour'));
  const minute = Number(get('minute'));
  const weekdayRaw = String(get('weekday') ?? 'Mon');
  const weekday = (WEEKDAYS.includes(weekdayRaw as any) ? weekdayRaw : 'Mon') as ZonedParts['weekday'];

  return {
    year: Number.isFinite(year) ? year : new Date().getFullYear(),
    month: Number.isFinite(month) ? month : 1,
    day: Number.isFinite(day) ? day : 1,
    hour: Number.isFinite(hour) ? hour : 0,
    minute: Number.isFinite(minute) ? minute : 0,
    weekday,
  };
}

export function getMarketDayKey(date: Date, spec: MarketSpec): string {
  const p = getZonedParts(date, spec.timeZone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

function partsToUtcMs(p: Pick<ZonedParts, 'year' | 'month' | 'day' | 'hour' | 'minute'>): number {
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, 0, 0);
}

/**
 * Convert a wall-clock time in a timezone into a UTC Date.
 * Uses a small iterative correction to handle DST without extra deps.
 */
export function zonedTimeToUtc(
  wallClock: Pick<ZonedParts, 'year' | 'month' | 'day'> & { hour: number; minute: number },
  timeZone: string
): Date {
  // Initial guess: interpret the wall-clock as if it's UTC.
  let guess = new Date(Date.UTC(wallClock.year, wallClock.month - 1, wallClock.day, wallClock.hour, wallClock.minute, 0, 0));

  // Refine (2 iterations is usually enough).
  for (let i = 0; i < 3; i++) {
    const observed = getZonedParts(guess, timeZone);
    const deltaMs = partsToUtcMs(observed) - partsToUtcMs(wallClock);
    if (Math.abs(deltaMs) < 30_000) break;
    guess = new Date(guess.getTime() - deltaMs);
  }

  return guess;
}

export function getMarketProgress(spec: MarketSpec, now: Date = new Date()): {
  isMarketDay: boolean;
  isMarketOpenNow: boolean;
  progress: number;
} {
  const p = getZonedParts(now, spec.timeZone);
  const isMarketDay = spec.openWeekdays.includes(p.weekday);
  if (!isMarketDay) return { isMarketDay: false, isMarketOpenNow: false, progress: 1 };

  const currentMinutes = p.hour * 60 + p.minute;
  if (currentMinutes <= spec.openMinutes) return { isMarketDay: true, isMarketOpenNow: false, progress: 0 };
  if (currentMinutes >= spec.closeMinutes) return { isMarketDay: true, isMarketOpenNow: false, progress: 1 };

  const denom = Math.max(1, spec.closeMinutes - spec.openMinutes);
  const raw = (currentMinutes - spec.openMinutes) / denom;
  const progress = Math.max(0, Math.min(1, raw));
  return { isMarketDay: true, isMarketOpenNow: true, progress };
}

export function getMarketSessionLabel(spec: MarketSpec, now: Date = new Date()): 'Market Open' | 'Pre-Market' | 'After Hours' {
  const p = getZonedParts(now, spec.timeZone);
  const isMarketDay = spec.openWeekdays.includes(p.weekday);
  if (!isMarketDay) return 'After Hours';

  const currentMinutes = p.hour * 60 + p.minute;
  if (currentMinutes < spec.openMinutes) {
    const start = spec.preMarketStartMinutes;
    if (start != null && currentMinutes >= start) return 'Pre-Market';
    return 'After Hours';
  }
  if (currentMinutes >= spec.closeMinutes) return 'After Hours';
  return 'Market Open';
}

export function getNextMarketOpenUtc(spec: MarketSpec, now: Date = new Date()): Date {
  const p = getZonedParts(now, spec.timeZone);
  const openHour = Math.floor(spec.openMinutes / 60);
  const openMinute = spec.openMinutes % 60;

  // Candidate: today at open (market tz)
  let candidate = zonedTimeToUtc({ year: p.year, month: p.month, day: p.day, hour: openHour, minute: openMinute }, spec.timeZone);

  const isTodayMarketDay = spec.openWeekdays.includes(p.weekday);
  if (isTodayMarketDay && now.getTime() < candidate.getTime()) {
    return candidate;
  }

  // Otherwise move forward day-by-day until the next open weekday.
  // We do this by advancing the local date via a UTC date, then re-reading zoned parts.
  let cursor = new Date(now.getTime());
  for (let i = 0; i < 10; i++) {
    cursor = new Date(cursor.getTime() + 24 * 60 * 60 * 1000);
    const cp = getZonedParts(cursor, spec.timeZone);
    if (!spec.openWeekdays.includes(cp.weekday)) continue;
    candidate = zonedTimeToUtc(
      { year: cp.year, month: cp.month, day: cp.day, hour: openHour, minute: openMinute },
      spec.timeZone
    );
    if (candidate.getTime() > now.getTime()) return candidate;
  }

  // Fallback: in case something weird happens, just return 24h ahead.
  return new Date(now.getTime() + 24 * 60 * 60 * 1000);
}
